import { useState } from "react";

import type { OnbStepDef } from "@/components/onboarding/OnbShell";

// DEBT-014: máquina de estados comum dos wizards de onboarding
// (WorkspaceOnboarding e OrgOnboarding). Centraliza stepId/completed/skipped/done
// + derivados (stepIdx, completedCount, isLast) e as transições genéricas
// (markCompleted/markSkipped → próximo passo ou done; goStep; goBack). A lógica
// específica de cada fluxo (quando dispara o accept/POST) fica nos componentes.
export interface StepWizard<Id extends string> {
  stepId: Id;
  setStepId: (id: Id) => void;
  completed: Record<string, boolean>;
  skipped: Record<string, boolean>;
  done: boolean;
  setDone: (v: boolean) => void;
  stepIdx: number;
  completedCount: number;
  isLast: boolean;
  /** Marca o passo atual como concluído e avança (ou finaliza se for o último). */
  markCompletedAndAdvance: () => void;
  /** Marca o passo atual como pulado e avança (ou finaliza se for o último). */
  markSkippedAndAdvance: () => void;
  /** Volta um passo (no-op se já no primeiro). */
  goBack: () => void;
  /** Navega direto pra um passo (e sai do estado "done"). Aceita string para casar
   *  com a assinatura de `OnbShell.onGoStep`. */
  goStep: (id: string) => void;
}

export function useStepWizard<Id extends string>(
  steps: readonly (OnbStepDef & { id: Id })[],
): StepWizard<Id> {
  const [stepId, setStepId] = useState<Id>(steps[0].id);
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  const [done, setDone] = useState(false);

  const stepIdx = steps.findIndex((s) => s.id === stepId);
  const completedCount = Object.keys(completed).length;
  const isLast = stepIdx === steps.length - 1;

  const advance = () => {
    const next = steps[stepIdx + 1];
    if (next) setStepId(next.id);
    else setDone(true);
  };

  const markCompletedAndAdvance = () => {
    setCompleted((c) => ({ ...c, [steps[stepIdx].id]: true }));
    advance();
  };

  const markSkippedAndAdvance = () => {
    setSkipped((s) => ({ ...s, [steps[stepIdx].id]: true }));
    advance();
  };

  const goBack = () => {
    if (stepIdx > 0) setStepId(steps[stepIdx - 1].id);
  };

  const goStep = (id: string) => {
    setStepId(id as Id);
    setDone(false);
  };

  return {
    stepId,
    setStepId,
    completed,
    skipped,
    done,
    setDone,
    stepIdx,
    completedCount,
    isLast,
    markCompletedAndAdvance,
    markSkippedAndAdvance,
    goBack,
    goStep,
  };
}
